import React, { useEffect } from 'react';
import { Box, Typography, Card, CardContent, Divider } from '@mui/material';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FileText } from 'lucide-react';
import { buildGeoespacialInventory } from '../../utils/reportCalculations/geoespacialInventory';
import ReportWidget from './ReportWidget';

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatArea = (value) => {
  const n = toNumberOrNull(value);
  if (n === null) return '-';
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  const n = toNumberOrNull(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const DEFAULT_CENTER = [-15.8, -47.9];
const DEFAULT_ZOOM = 5;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

function FitBoundsOnFeatures({ features, refreshToken }) {
  const map = useMap();

  useEffect(() => {
    if (!features || !Array.isArray(features) || features.length === 0) return;

    try {
      if (map && typeof map.invalidateSize === 'function') {
        map.invalidateSize();
      }

      const layer = L.geoJSON({ type: 'FeatureCollection', features });
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [4, 4] });
      }
    } catch (_e) {
      // falha de ajuste de bounds não deve quebrar o relatório
    }
  }, [features, map, refreshToken]);

  return null;
}

function MiniLayerMap({ areaFeatures, layerFeatures, disabled = false, refreshToken }) {
  const areaArr = Array.isArray(areaFeatures) ? areaFeatures : [];
  const layerArr = Array.isArray(layerFeatures) ? layerFeatures : [];
  const allFeatures = [...areaArr, ...layerArr];
  const hasAnyFeature = allFeatures.length > 0;

  if (!hasAnyFeature) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 1,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Sem geometria
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: disabled ? 'grey.100' : 'white',
      }}
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={3}
        maxZoom={19}
        attributionControl={false}
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        boxZoom={false}
        keyboard={false}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          pointerEvents: 'none',
        }}
      >
        {areaArr.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: areaArr }}
            style={() => ({
              color: disabled ? '#b0bec5' : '#f9a825',
              weight: 2,
              fillColor: disabled ? '#eceff1' : '#fff9c4',
              fillOpacity: disabled ? 0.3 : 0.35,
            })}
          />
        )}

        {layerArr.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: layerArr }}
            style={() => ({
              color: disabled ? '#90a4ae' : '#0d47a1',
              weight: 2,
              fillColor: disabled ? '#cfd8dc' : '#42a5f5',
              fillOpacity: disabled ? 0.4 : 0.5,
            })}
          />
        )}

        <FitBoundsOnFeatures features={allFeatures} refreshToken={refreshToken} />
      </MapContainer>
    </Box>
  );
}

const ENV_CARD_ORDER = [
  'ÁREA DO IMÓVEL',
  'APP',
  'RESERVA LEGAL',
  'USO RESTRITO',
  'ÁREA CONSOLIDADA',
  'VEGETAÇÃO NATIVA',
  'ÁREA NÃO CLASSIFICADA',
  'ÁREA DE SERVIDÃO',
  'ÁREA DE POUSIO',
];

const ENV_CARD_LABELS = {
  'ÁREA DO IMÓVEL': 'Área do Imóvel',
  APP: 'APP',
  'RESERVA LEGAL': 'Reserva Legal',
  'USO RESTRITO': 'Uso Restrito',
  'ÁREA CONSOLIDADA': 'Área Consolidada',
  'VEGETAÇÃO NATIVA': 'Vegetação Nativa',
  'ÁREA NÃO CLASSIFICADA': 'Área Não Classificada',
  'ÁREA DE SERVIDÃO': 'Área de Servidão Administrativa',
  'ÁREA DE POUSIO': 'Área de Pousio',
};

function normalizeId(value, fallbackPrefix) {
  const base = value || fallbackPrefix || '';
  return String(base)
    .normalize('NFD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

export default function ReportPrintGeoespacialDetails({
  snapshot,
  printRefreshToken,
  mapSnapshots,
}) {
  const { carItems, sigefItems } = buildGeoespacialInventory(snapshot);

  const hasAnyCar = Array.isArray(carItems) && carItems.length > 0;
  const hasAnySigef = Array.isArray(sigefItems) && sigefItems.length > 0;

  if (!hasAnyCar && !hasAnySigef) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma informação geoespacial (CAR/SIGEF) disponível para este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FileText size={20} style={{ marginRight: 8 }} />
        <Typography variant="h6">Geoespacial - Inventário de Áreas Registradas</Typography>
      </Box>

      {/* Seção CAR */}
      <ReportWidget sectionKey="geoespacial" widgetKey="geos_car_inventory">
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            borderLeft: 4,
            borderLeftColor: 'primary.main',
            boxShadow: 1,
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              CAR - Área do Imóvel por código
            </Typography>

            {!hasAnyCar && (
              <Typography variant="body2" color="text.secondary">
                Nenhum registro de CAR encontrado para este relatório.
              </Typography>
            )}

            {hasAnyCar && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {carItems.map((item, index) => {
                  const codigo = item.codigo || '';
                  const areaImovelFeatures = Array.isArray(item.areaImovelFeatures)
                    ? item.areaImovelFeatures
                    : [];

                  const codigoId = normalizeId(codigo, `car_${index}`);

                  return (
                    <Box
                      key={codigo || index}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        p: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Código CAR: {codigo || '-'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Área do Imóvel declarada (CAR): {formatArea(item.areaImovelDeclaradaHa)} ha
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Área do Imóvel calculada (geometria):{' '}
                        {formatArea(item.areaImovelCalculadaHa)} ha
                      </Typography>

                      <Box
                        sx={{
                          mt: 1.5,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                          gap: 2,
                        }}
                      >
                        {ENV_CARD_ORDER.map((alias) => {
                          const layer =
                            alias === 'ÁREA DO IMÓVEL'
                              ? item.areaImovelLayer
                              : item.layers && item.layers[alias];

                          const declaredHa =
                            layer && layer.declaredHa != null
                              ? layer.declaredHa
                              : alias === 'ÁREA DO IMÓVEL'
                                ? item.areaImovelDeclaradaHa
                                : null;
                          const calculatedHa =
                            layer && layer.calculatedHa != null ? layer.calculatedHa : null;
                          const deltaHa = layer && layer.deltaHa != null ? layer.deltaHa : null;
                          const deltaPct = layer && layer.deltaPct != null ? layer.deltaPct : null;

                          const areaFeatures = areaImovelFeatures;
                          const layerFeatures =
                            alias === 'ÁREA DO IMÓVEL'
                              ? areaImovelFeatures
                              : layer && Array.isArray(layer.features)
                                ? layer.features
                                : [];

                          const isEnvTheme = alias !== 'ÁREA DO IMÓVEL';
                          const declaredNum = toNumberOrNull(declaredHa);
                          const calculatedNum = toNumberOrNull(calculatedHa);
                          const isDeclaredZeroOrEmpty = declaredNum === null || declaredNum === 0;
                          const isCalculatedZeroOrEmpty =
                            calculatedNum === null || calculatedNum === 0;
                          const isDisabled =
                            isEnvTheme && isDeclaredZeroOrEmpty && isCalculatedZeroOrEmpty;

                          const aliasId = normalizeId(alias, 'alias');
                          const mapId = `geos-car-${codigoId}-${aliasId}`;
                          const mapUrl = mapSnapshots && mapSnapshots[mapId];

                          return (
                            <Box
                              key={alias}
                              sx={{
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                bgcolor: isDisabled ? 'grey.100' : 'background.paper',
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {ENV_CARD_LABELS[alias] || alias}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'stretch',
                                  gap: 1.5,
                                  minHeight: 160,
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: '0 0 50%',
                                    height: '100%',
                                  }}
                                >
                                  {mapUrl ? (
                                    <Box
                                      component="img"
                                      src={mapUrl}
                                      alt={`Mapa ${codigo || '-'} - ${alias}`}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        display: 'block',
                                      }}
                                    />
                                  ) : (
                                    <MiniLayerMap
                                      areaFeatures={areaFeatures}
                                      layerFeatures={layerFeatures}
                                      disabled={isDisabled}
                                      refreshToken={printRefreshToken}
                                    />
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    flex: '0 0 50%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    Área Cadastrada (ha): {formatArea(declaredHa)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Área Calculada (ha): {formatArea(calculatedHa)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Δ área (ha): {formatArea(deltaHa)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Δ área (%): {formatPercent(deltaPct)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </ReportWidget>

      {/* Seção SIGEF */}
      <ReportWidget sectionKey="geoespacial" widgetKey="geos_sigef_inventory">
        <Card
          sx={{
            mb: 1,
            borderRadius: 2,
            borderLeft: 4,
            borderLeftColor: 'secondary.main',
            boxShadow: 1,
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              SIGEF - Parcelas e cobertura sobre o imóvel
            </Typography>

            {!hasAnySigef && (
              <Typography variant="body2" color="text.secondary">
                Nenhum registro SIGEF encontrado para este relatório.
              </Typography>
            )}

            {hasAnySigef && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sigefItems.map((item, index) => {
                  const codigo = item.codigo || '';
                  const parcels = Array.isArray(item.parcels) ? item.parcels : [];
                  const baseFeatures = Array.isArray(item.baseFeatures) ? item.baseFeatures : [];

                  const codigoId = normalizeId(codigo, `sigef_${index}`);

                  return (
                    <Box
                      key={codigo || index}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        p: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Código SIGEF: {codigo || '-'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Área total SIGEF (ha): {formatArea(item.areaTotalSigefHa)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Parcelas SIGEF vinculadas: {item.parcelsCount}
                      </Typography>

                      {parcels.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Nenhuma parcela SIGEF com geometria processada.
                        </Typography>
                      )}

                      {parcels.length > 0 && (
                        <Box
                          sx={{
                            mt: 1.5,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: 2,
                          }}
                        >
                          {parcels.map((parcel, parcelIndex) => {
                            const parcelFeatures = Array.isArray(parcel.features)
                              ? parcel.features
                              : [];
                            const areaParcela = parcel.areaHa;
                            let pctOfTotal = null;
                            const total = toNumberOrNull(item.areaTotalSigefHa);
                            const parcelaNum = toNumberOrNull(areaParcela);
                            if (total && parcelaNum !== null) {
                              pctOfTotal = (parcelaNum / total) * 100;
                            }

                            const parcelaId = normalizeId(parcel.parcela, `p_${parcelIndex}`);
                            const mapId = `geos-sigef-${codigoId}-parcela-${parcelaId}`;
                            const mapUrl = mapSnapshots && mapSnapshots[mapId];

                            return (
                              <Box
                                key={parcel.parcela}
                                sx={{
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  p: 1.5,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.75,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Parcela {parcel.parcela}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'stretch',
                                    gap: 1.5,
                                    minHeight: 160,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: '0 0 50%',
                                      height: '100%',
                                    }}
                                  >
                                    {mapUrl ? (
                                      <Box
                                        component="img"
                                        src={mapUrl}
                                        alt={`Mapa SIGEF ${codigo || '-'} - parcela ${
                                          parcel.parcela || '-'
                                        }`}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                          display: 'block',
                                        }}
                                      />
                                    ) : (
                                      <MiniLayerMap
                                        areaFeatures={baseFeatures}
                                        layerFeatures={parcelFeatures}
                                        refreshToken={printRefreshToken}
                                      />
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      flex: '0 0 50%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      Área total SIGEF (ha): {formatArea(item.areaTotalSigefHa)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Área da parcela (ha): {formatArea(areaParcela)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      % da área total SIGEF: {formatPercent(pctOfTotal)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </ReportWidget>

      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
